import { useCallback, useRef } from 'react'

/**
 * The chat an upload belongs to, shared by the upload and send hooks.
 *
 * `ensureChatId` answers differently per mount: the thread composer returns its
 * active thread every time, while home mints one. Only a provisional (home) id is
 * remembered — the thread composer keeps the same React position across thread
 * switches, so caching there would send thread B's upload into thread A.
 */
export function useComposerChatSession(ensureChatId) {
  const current = useRef(/** @type {object | null} */ (null))

  const resolve = useCallback(
    async (fileName) => {
      const chat = await ensureChatId?.(fileName)
      if (chat?.provisional) current.current = chat
      return chat ?? null
    },
    [ensureChatId],
  )

  // Cleared on send: the provisional chat is handed over and stops being pending.
  const take = useCallback(() => {
    const chat = current.current
    current.current = null
    return chat
  }, [])

  return { resolve, take }
}
